import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, Editor, AnyExtension } from '@tiptap/react';
import { SlashCommands } from '../extensions/SlashCommands';
import { defaultItems } from '../components/SlashCommandsList';
import { convertReferenceNodesToText } from '../utils/referenceUtils';
import { REFERENCE_DOUBLE_MUSTACHE_PATTERN } from '../regex';
import { UseTipTapEditorProps } from '../types/editorTypes';
import NoNewLine from '../extensions/NoNewLine';
import {
  processAllReferences as processAllReferencesUtil,
  processHtmlForPreview as processHtmlForPreviewUtil,
} from '../utils/referenceTransformUtils';
import ExtensionKit from '../extensions/extension-kit';
import { isJSON } from 'utilities/index';

/**
 * Core TipTap editor hook for setting up and managing a TipTap WYSIWYG editor
 * with various extensions and content handling capabilities
 */
export const useTipTapEditor = ({
  readOnly = false,
  multiline = true,
  placeholder,
  initialContent = '',
  onChange,
  onFocus,
  onBlur,
  slashCommandItems = defaultItems,
  onError,
}: UseTipTapEditorProps) => {
  const [isInserting, setIsInserting] = useState(false);
  const editorUpdateTimeout = useRef<NodeJS.Timeout | null>(null);

  // Add ref to track editor instance for accessing in other components
  const editorRef = useRef<Editor | null>(null);

  // Process HTML to handle empty paragraphs better for preview mode
  const processHtmlForPreview = useCallback((html: string): string => {
    return processHtmlForPreviewUtil(html);
  }, []);

  // Add a function to manually transform references after editor is mounted
  const processAllReferences = useCallback((editor: Editor) => {
    processAllReferencesUtil(editor);
  }, []);

  // Format content for database storage - transform reference spans to mustache braces
  const formatContentForDatabase = useCallback((html: string): string => {
    if (!html) return '';

    // Use the reference utility function to convert spans to mustache format
    return convertReferenceNodesToText(html);
  }, []);

  // Function to insert a reference at the current cursor position
  const insertReferenceAtCursor = useCallback((referenceId: string) => {
    if (!editorRef.current) return;

    // Basic cleanup - just make sure there are no double braces
    const cleanId = referenceId.replace(/^\{\{|\}\}$/g, '');

    // Check if it has a resource prefix (inputs. or steps.)
    const hasResourcePrefix = cleanId.match(/^(inputs|steps)\./);

    // If no resource prefix, add a default one (inputs.)
    const finalId = hasResourcePrefix ? cleanId : `inputs.${cleanId}`;

    // Insert the reference followed by a space for better cursor positioning
    editorRef.current
      .chain()
      .focus()
      .insertContent([
        {
          type: 'reference',
          attrs: {
            id: finalId,
          },
        },
        {
          type: 'text',
          text: ' ', // Add a space after the reference
        },
      ])
      .run();

    // Fix cursor position and ensure references are properly decorated
    setTimeout(() => {
      if (editorRef.current) {
        processAllReferencesUtil(editorRef.current);
        editorRef.current.commands.focus(); // Keep focus on editor
      }
    }, 50);
  }, []);

  // Initialize the editor with our custom extensions
  const editor = useEditor(
    {
      // Ensure ExtensionKit includes all necessary extensions
      extensions: [
        ...ExtensionKit(),
        // Add NoNewLine extension when multiline is false
        ...(!multiline ? [NoNewLine] : []),
        // Make sure SlashCommands is configured properly
        SlashCommands.configure({
          suggestion: {
            items: () => slashCommandItems,
          },
        }),
      ].filter((e): e is AnyExtension => e !== undefined),
      enableContentCheck: true,
      onContentError: (error) => {
        console.log('content error:', error);
        if (onError) {
          onError('There is an error loading content');
        }
      },
      editorProps: {
        attributes: {
          autocomplete: 'off',
          autocorrect: 'off',
          autocapitalize: 'off',
          class: 'min-h-full',
        },
      },
      content: initialContent,
      editable: !readOnly,
      onUpdate: ({ editor }) => {
        // Store the editor instance for later reference
        editorRef.current = editor;

        // Get JSON representation of the content
        const jsonContent = editor.getJSON();

        // Store reference to the editor instance on the DOM element
        // This helps other components access the editor instance
        if (editor.options.element) {
          (editor.options.element as any).__editor = editor;
          editor.options.element.setAttribute('data-tiptap-editor', 'true');

          // Dispatch a custom event to notify parent components that the editor is focused
          editor.options.element.dispatchEvent(
            new CustomEvent('tiptap-editor-focused', {
              bubbles: true,
              detail: { editorElement: editor.options.element },
            })
          );
        }

        // Debounce the onChange to prevent racing conditions
        if (editorUpdateTimeout.current) {
          clearTimeout(editorUpdateTimeout.current);
        }

        editorUpdateTimeout.current = setTimeout(() => {
          // Process references before passing to onChange
          processAllReferences(editor);
          // Simply pass the JSON content directly
          onChange && onChange(JSON.stringify(jsonContent));
        }, 500);
      },
      onFocus: () => {
        if (onFocus) {
          onFocus();
        }
      },
      onBlur: (event) => {
        if (onBlur) {
          onBlur(event);
        }
      },
    },
    []
  );
  console.log('editor schema', editor?.schema);
  // Update content when initialContent changes from outside
  useEffect(() => {
    if (
      editor &&
      initialContent !== undefined &&
      // Check if editor has content and compare differently based on type
      ((typeof initialContent === 'string' &&
        initialContent !== editor.getHTML()) ||
        typeof initialContent === 'object')
    ) {
      // If editor is not focused (to avoid disrupting user editing)
      if (!editor.isFocused) {
        try {
          // Set the content - TipTap can accept both string and JSON object
          if (isJSON(initialContent || '')) {
            editor.commands.setContent(JSON.parse(initialContent));
          } else {
            editor.commands.setContent(initialContent);
          }

          // Process references after setting content
          setTimeout(() => {
            processAllReferences(editor);
          }, 50);
        } catch (error) {
          onError?.('There was an error loading editor content.');
        }
      }
    }
  }, [initialContent, editor, processAllReferences]);

  // Add fallback reference transformation
  useEffect(() => {
    if (!editor || readOnly) return;

    let isMounted = true;

    try {
      if (!isMounted || !editor) return;

      // Get current HTML content
      const content = editor.getHTML();

      // Check if there are still references in the content after automatic transformation
      const matches = content.match(REFERENCE_DOUBLE_MUSTACHE_PATTERN);

      if (matches && matches.length > 0) {
        processAllReferences(editor);
      }
    } catch (error) {
      console.error('Error checking for untransformed references:', error);
    }

    return () => {
      isMounted = false;

      // Clean up any pending timers
      if (editorUpdateTimeout.current) {
        clearTimeout(editorUpdateTimeout.current);
      }
    };
  }, [editor, readOnly, processAllReferences]);

  return {
    editor,
    editorRef,
    isInserting,
    setIsInserting,
    formatContentForDatabase,
    processHtmlForPreview,
    processAllReferences,
    insertReferenceAtCursor,
  };
};
